import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/track_usage"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static values = {
    side: {
      type: String,
      default: "right"
    },
    scroll: {
      type: Boolean,
      default: true
    },
    keyboard: {
      type: Boolean,
      default: true
    },
    backdrop: {
      type: Boolean,
      default: false
    }
  }

  static targets = ["fixedTop", "scrollable"]

  initialize() {
    this.fixScrollableHeight = this.fixScrollableHeight.bind(this)
    this.afterHidden = this.afterHidden.bind(this)
  }

  connect() {
    // The sidebar is re-rendered into its frame on every comment post or
    // resolve (turbo_stream.update :right_sidebar), which reconnects this
    // controller while the sidebar is already open. The frame's aria-hidden
    // (set by show/afterHidden) tells a fresh open from a re-render.
    const wasOpen = this.element.parentElement?.getAttribute("aria-hidden") === "false"

    this._bsOffcanvas = new bootstrap.Offcanvas(this.element, this._offCanvasOptions)
    document.addEventListener("scroll", this.fixScrollableHeight)
    this.element.addEventListener("hidden.bs.offcanvas", this.afterHidden)
    this.show()
    if (!wasOpen) track("sidebar_opened", { kind: [...this.element.classList].find(c => c.endsWith("-sidebar")) || this.element.id })
  }

  disconnect() {
    document.removeEventListener("scroll", this.fixScrollableHeight)
    this.element.removeEventListener("hidden.bs.offcanvas", this.afterHidden)

    // No hide() here. Offcanvas#hide finishes from a transition callback
    // queued on a timer, but dispose() nulls Bootstrap's element reference
    // straight away — so hiding and then disposing makes that callback throw
    // once the transition lands. dispose() already removes the backdrop,
    // releases the focus trap and restores the scrollbar, which is the whole
    // of what disconnect needs. Deliberate hides still go through teardown().
    this._bsOffcanvas.dispose()
    this._bsOffcanvas = null
  }

  show() {
    this.fixScrollableHeight()
    this._bsOffcanvas.show()
    this.element.parentElement.setAttribute("aria-hidden", "false")
  }

  hide() {
    this._bsOffcanvas.hide()
  }

  fixScrollableHeight() {
    if (!this.fixedTopTarget) return

    const maxHeight = window.innerHeight - this.fixedTopTarget.getBoundingClientRect().bottom
    this.scrollableTarget.style.overflowY = "auto"
    this.scrollableTarget.style.maxHeight = `${maxHeight}px`
  }

  teardown() {
    this.hide()
  }

  afterHidden() {
    this.element.parentElement.setAttribute("aria-hidden", "true")
  }

  get _offCanvasOptions() {
    return {
      scroll: this.scrollValue,
      backdrop: this.backdropValue,
      keyboard: this.keyboardValue
    }
  }
}